import React, { useEffect, useState } from "react";
import { Text } from "@vibe/core";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import logger from "../../lib/logger";
import { connectSubitem, createMissingSubitem, unlinkSubitem } from "../../utils/requests/subitemDiscrepancyRequests";
import type { BoardHeader } from "./DiscrepancyGrid/discrepancyGrid.interface";

// Mirrors the engine's ResyncResult / UnlinkResult (simb-integration-ms's
// subitem-resync.interface.ts:188-241) — not imported, since that package is a separate
// service with its own build. `succeeded` and `failed` are NOT mutually exclusive for
// createMissing: a board whose Monday create landed but whose subsequent `insertMany` failed
// appears in BOTH (Phase 3a Decision D6) — `succeeded` because Monday really changed (so the
// flags cache must invalidate), `failed` because the sync row is missing (so the user learns
// the created subitem is now unmanaged, not silently absent). Render both; never collapse to
// `succeeded.length > 0 ? "Success" : "Failure"`.
export interface ResyncResult {
  succeeded: string[];
  failed: Array<{ boardId: string; columnKey?: string; reason: string }>;
}

// `deletedCount` is Mongo's own deleteMany count — the ONLY authoritative measure of what
// unlink actually removed. `succeeded` here records boards this call ATTEMPTED (the
// verified-absent set after any boardIds narrowing), not Mongo's outcome, so it can be
// non-empty while deletedCount is 0 (e.g. a concurrent unlink already removed the rows).
// `retained` lists boards whose Monday subitem still exists and was therefore left untouched —
// the reassuring half a user who fears data loss needs to see.
export interface UnlinkResult extends ResyncResult {
  deletedCount: number;
  retained: string[];
}

// Mirrors the engine's ConnectResult. `linked` is whether THIS call inserted the sync link:
// false means a row for the subitem already existed (a double click, a stale grid, a
// teammate who connected it first) and nothing was written anywhere — benign, and worded as
// "already connected" rather than "connected". `succeeded`/`failed` are the boards the copies
// were created on / refused for, the same non-exclusive contract as createMissing, because
// the engine delegates that half to it.
export interface ConnectResult extends ResyncResult {
  subitemSyncId: string;
  boardId: string;
  linked: boolean;
}

export interface SubitemCreateRequest {
  kind: "create";
  parentSyncId: string;
  subitemSyncId: string;
  subitemName: string;
  /** The sole source of which boards to create on — passed through verbatim from the
   * terminal row's `missingOnBoards` (subitemScanAdapter.ts), never re-derived here. */
  targetBoardIds: string[];
  boards: BoardHeader[];
}

export interface SubitemUnlinkRequest {
  kind: "unlink";
  parentSyncId: string;
  subitemSyncId: string;
  subitemName: string;
  /** Narrows the delete to these boards rather than every mirror the server verifies absent —
   * always the subitem's own linked board set. orphaned_record carries no per-board evidence
   * of which board went stale (subitemScanAdapter.ts's own comment on `missingOnBoards` being
   * `[]` for this state), so this is the narrowest set the frontend can honestly name. It is
   * necessarily WIDER than what will actually be deleted — the service intersects it with its
   * own verified-absent set, so it can only narrow — which is why the confirmation presents it
   * as the boards that will be CHECKED, not as the deletion set. */
  boardIds: string[];
  boards: BoardHeader[];
}

export interface SubitemConnectRequest {
  kind: "connect";
  parentSyncId: string;
  /** The synthetic `unlinked:<id>` sync id, for row identity only — never sent. */
  subitemSyncId: string;
  subitemName: string;
  /** The Monday item id the engine reported on `location` — the one thing the server needs. */
  subitemId: string;
  /** Where the subitem lives, as Monday reported it. Shown, so the user knows what they are
   * connecting; the other boards in `boards` are where copies will be created. */
  boardId: string;
  boardName: string;
  boards: BoardHeader[];
}

export type SubitemDestructiveRequest = SubitemCreateRequest | SubitemUnlinkRequest | SubitemConnectRequest;

interface SubitemDestructiveModalProps {
  request: SubitemDestructiveRequest | null;
  token: string | null;
  onClose: () => void;
  /** Called when a refetch could return something new, so the caller can revalidate via
   * `mutate()`. For create that is `succeeded.length > 0` (mirrors SubitemResyncModal / the
   * engine's own flags-invalidation gate) OR an all-empty result, which is the idempotency
   * guard proving every requested board already has a mirror and so that the row this was
   * launched from is stale. For unlink it is `deletedCount > 0` — `succeeded` there is not
   * proof anything was actually deleted. For connect it is ALWAYS: either the link was just
   * written (the row must stop saying "unlinked") or it already existed (the row is stale). */
  onResynced: () => void;
}

/**
 * A create result reporting nothing at all — no board created, no board refused. This is the
 * service's idempotency guard returning 200 because every requested board already has a mirror
 * (subitem-resync.service.ts:993-1008): a stale scan, a double click, or a teammate creating
 * first. Benign and deterministically reachable, and it used to render an empty flex div under
 * a "Done" button.
 */
const isNoChangeCreateResult = (response: ResyncResult): boolean =>
  response.succeeded.length === 0 && response.failed.length === 0;

const boardName = (boards: BoardHeader[], boardId: string): string =>
  boards.find((board) => board.boardId === boardId)?.boardName ?? boardId;

const boardNamesList = (boards: BoardHeader[], boardIds: string[]): string =>
  boardIds.map((boardId) => boardName(boards, boardId)).join(", ");

const renderFailed = (boards: BoardHeader[], failed: ResyncResult["failed"]) =>
  failed.length > 0 ? (
    <div>
      <Text type="text1" weight="medium" color="negative">
        Not sent:
      </Text>
      <ul style={{ margin: 0, paddingInlineStart: "var(--spacing-medium)" }}>
        {failed.map((failure, index) => (
          <li key={`${failure.boardId}-${failure.columnKey ?? "board"}-${index}`}>
            <Text type="text2" weight="medium">
              {failure.columnKey
                ? `${boardName(boards, failure.boardId)} — ${failure.columnKey}`
                : boardName(boards, failure.boardId)}
            </Text>
            <Text type="text2" color="secondary">
              {failure.reason}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

/**
 * Confirmation + result dialog for the three subitem actions that change what SIMB manages:
 * creating a missing subitem on the boards it's absent from, unlinking a subitem whose Monday
 * record is already gone, and (2026-09-11) connecting a subitem SIMB has no record of — which
 * links it AND creates its copies, so it belongs here with the other Monday-writing actions
 * rather than in SubitemResyncModal. A separate component from SubitemResyncModal (Task 7), not an extension of
 * it — bending that modal to also carry an irreversible delete is how a shared component
 * grows two personalities. Same interaction shape (preview the consequence, Confirm/Cancel, a
 * loading Confirm, render the result, never patch state optimistically — Phase 3a already
 * invalidates the server-side flags cache on a successful write, so `onResynced` refetches the
 * truth) but its own copy: both actions here name the consequence rather than asking a generic
 * "are you sure", since neither is reversible from inside the app.
 */
const SubitemDestructiveModal: React.FC<SubitemDestructiveModalProps> = ({
  request,
  token,
  onClose,
  onResynced,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResyncResult | UnlinkResult | ConnectResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Scoped to whichever request is currently open — never carried over, never persisted
  // anywhere the plan forbids (localStorage, Redis, Mongo).
  useEffect(() => {
    setResult(null);
    setError(null);
    setLoading(false);
  }, [request]);

  const isOpen = request !== null;

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    if (!request || !token) return;
    setLoading(true);
    setError(null);
    try {
      if (request.kind === "create") {
        const response: ResyncResult = await createMissingSubitem(
          token,
          request.parentSyncId,
          request.subitemSyncId,
          request.targetBoardIds,
        );
        setResult(response);
        // Mirrors the engine's own flags-invalidation gate (Decision D6): Monday changed
        // whenever succeeded is non-empty, even if the same board also appears in failed.
        //
        // The second half covers the idempotency-guard result (both arrays empty, meaning every
        // requested board already has a mirror — subitem-resync.service.ts:993-1008). Nothing
        // was written, but the scan this action was launched from is provably stale, so it must
        // refresh; without this the `missing` row survives a refetch-less close and the user is
        // left staring at a discrepancy that no longer exists.
        if (response.succeeded.length > 0 || isNoChangeCreateResult(response)) {
          onResynced();
        }
      } else if (request.kind === "connect") {
        const response: ConnectResult = await connectSubitem(token, request.parentSyncId, request.subitemId);
        setResult(response);
        // Unconditional — see onResynced's doc comment. The engine invalidates its own cached
        // verdict on every connect for the same reason: the DATABASE changed (or was already
        // ahead of the grid) whether or not any Monday write followed.
        onResynced();
      } else {
        const response: UnlinkResult = await unlinkSubitem(
          token,
          request.parentSyncId,
          request.subitemSyncId,
          request.boardIds,
        );
        setResult(response);
        // deletedCount, not succeeded.length, is the only proof Mongo actually removed a
        // record — see the doc comment on UnlinkResult above.
        if (response.deletedCount > 0) {
          onResynced();
        }
      }
    } catch (err: any) {
      logger.error(err?.message ?? "Error processing subitem", { error: err });
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderCreateResult = (response: ResyncResult) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-medium)" }}>
      {/* See isNoChangeCreateResult: the most likely benign outcome of an irreversible-intent
          action used to show the user nothing at all. `onResynced` fires for this branch too
          (see handleConfirm), so the stale row it was launched from actually refreshes. */}
      {isNoChangeCreateResult(response) ? (
        <Text type="text1" element="p">
          Nothing needed to change — those subitems already exist.
        </Text>
      ) : null}
      {response.succeeded.length > 0 ? (
        <div>
          <Text type="text1" weight="medium">
            Created on:
          </Text>
          <ul style={{ margin: 0, paddingInlineStart: "var(--spacing-medium)" }}>
            {response.succeeded.map((boardId) => (
              <li key={boardId}>
                <Text type="text2">{boardName(request!.boards, boardId)}</Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {renderFailed(request!.boards, response.failed)}
    </div>
  );

  const renderUnlinkResult = (response: UnlinkResult) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-medium)" }}>
      <Text type="text1" weight="medium">
        {`${response.deletedCount} record${response.deletedCount === 1 ? "" : "s"} removed.`}
      </Text>
      {response.retained.length > 0 ? (
        <div>
          <Text type="text1" weight="medium">
            Left alone — still exists in Monday:
          </Text>
          <ul style={{ margin: 0, paddingInlineStart: "var(--spacing-medium)" }}>
            {response.retained.map((boardId) => (
              <li key={boardId}>
                <Text type="text2">{boardName(request!.boards, boardId)}</Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {renderFailed(request!.boards, response.failed)}
    </div>
  );

  const renderConnectResult = (response: ConnectResult, connectRequest: SubitemConnectRequest) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-medium)" }}>
      <Text type="text1" weight="medium">
        {response.linked
          ? `"${connectRequest.subitemName}" on ${connectRequest.boardName} is now synced.`
          : `"${connectRequest.subitemName}" was already connected — nothing needed to change.`}
      </Text>
      {response.succeeded.length > 0 ? (
        <div>
          <Text type="text1" weight="medium">
            Copies created on:
          </Text>
          <ul style={{ margin: 0, paddingInlineStart: "var(--spacing-medium)" }}>
            {response.succeeded.map((boardId) => (
              <li key={boardId}>
                <Text type="text2">{boardName(connectRequest.boards, boardId)}</Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {renderFailed(connectRequest.boards, response.failed)}
    </div>
  );

  /**
   * Names WHERE the subitem is (the thing the old copy could not say) and what Connect will
   * do to the other boards, since creating copies is a Monday write the user should see
   * coming. The target list is the parent's other subitem boards — the server derives the
   * same set from its own database and ignores anything the client might send, so this is a
   * preview, not an instruction.
   */
  const renderConnectConfirmation = (connectRequest: SubitemConnectRequest) => {
    const otherBoards = connectRequest.boards.filter((board) => board.boardId !== connectRequest.boardId);
    const count = otherBoards.length;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
        <Text type="text1" element="p">
          {`"${connectRequest.subitemName}" lives on `}
          <Text type="text1" weight="bold" element="span">
            {connectRequest.boardName}
          </Text>
          {` but isn't part of the sync yet, so changes to it don't reach the other boards.`}
        </Text>
        <Text type="text1" element="p">
          {count > 0
            ? `Connecting it starts syncing it and creates ${count === 1 ? "a copy" : `${count} copies`} on:`
            : "Connecting it starts syncing it. This item is only on one board, so no copies are needed yet."}
        </Text>
        {count > 0 ? (
          <Text type="text1" weight="bold">
            {otherBoards.map((board) => board.boardName).join(", ")}
          </Text>
        ) : null}
        <Text type="text2" color="secondary" element="p">
          Nothing on Monday is changed or removed — the copies are new subitems with this one&apos;s
          name and values.
        </Text>
        <Text type="text1" element="p">
          Do you wish to proceed?
        </Text>
        {error ? (
          <Text type="text2" color="negative" element="p">
            {error}
          </Text>
        ) : null}
      </div>
    );
  };

  const renderCreateConfirmation = (createRequest: SubitemCreateRequest) => {
    const count = createRequest.targetBoardIds.length;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
        <Text type="text1" element="p">
          {count > 0
            ? `This will create ${count} new subitem${count === 1 ? "" : "s"} for "${createRequest.subitemName}" on ${count} board${count === 1 ? "" : "s"}:`
            : `"${createRequest.subitemName}" has no boards left to create on — there is nothing to do.`}
        </Text>
        {count > 0 ? (
          <Text type="text1" weight="bold">
            {boardNamesList(createRequest.boards, createRequest.targetBoardIds)}
          </Text>
        ) : null}
        {count > 0 ? (
          <Text type="text1" element="p">
            Do you wish to proceed?
          </Text>
        ) : null}
        {error ? (
          <Text type="text2" color="negative" element="p">
            {error}
          </Text>
        ) : null}
      </div>
    );
  };

  /**
   * An `orphaned_record` fires precisely when SOME of a subitem's mirrors are gone
   * (subitem-classifier.ts:76 — `present.length < rows.length`), never necessarily all of them,
   * and `boardIds` is the subitem's FULL linked board set because the payload carries no
   * per-board evidence of which mirror went stale. So this list is always wider than what gets
   * deleted, and the old leading sentence ("The Monday subitem behind X no longer exists") was
   * unconditionally absolute about a subitem that is only partly gone.
   *
   * The write itself was never wrong — the service runs two fail-closed Monday probes and
   * intersects `boardIds` with the verified-absent set (subitem-resync.service.ts:1616-1690), so
   * a supplied list can only narrow. What was wrong is that pre-confirm copy overstated the
   * scope of an IRREVERSIBLE action, and the truth only arrived afterwards under "Left alone —
   * still exists in Monday". The list is therefore labelled as what it is — the boards that will
   * be CHECKED — and the full set is still sent, which is correct behaviour.
   */
  const renderUnlinkConfirmation = (unlinkRequest: SubitemUnlinkRequest) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
      <Text type="text1" element="p">
        {`SIMB can't see at least one board's Monday subitem behind "${unlinkRequest.subitemName}" with your permissions — it may have been deleted, or it may be hidden from you. Unlinking re-checks Monday with your access and with another user's, and removes only SIMB's stale sync records — the ones whose Monday subitem is confirmed missing. A subitem that still exists, or that is hidden from you, is left untouched, and nothing is deleted from Monday.`}
      </Text>
      <Text type="text2" weight="medium">
        Boards checked:
      </Text>
      <Text type="text1" weight="bold">
        {boardNamesList(unlinkRequest.boards, unlinkRequest.boardIds)}
      </Text>
      <Text type="text2" color="secondary" element="p">
        You&apos;ll see exactly which records were removed, and which boards were left alone,
        once this finishes.
      </Text>
      <Text type="text1" element="p">
        Do you wish to proceed?
      </Text>
      {error ? (
        <Text type="text2" color="negative" element="p">
          {error}
        </Text>
      ) : null}
    </div>
  );

  const confirmDisabled = !token || (request?.kind === "create" && request.targetBoardIds.length === 0);

  const TITLES: Record<SubitemDestructiveRequest["kind"], string> = {
    create: "Create missing subitem",
    unlink: "Unlink subitem",
    connect: "Connect subitem",
  };
  const CONFIRM_LABELS: Record<SubitemDestructiveRequest["kind"], string> = {
    create: "Create",
    unlink: "Unlink",
    connect: "Connect",
  };

  const renderBody = (open: SubitemDestructiveRequest) => {
    if (result) {
      if (open.kind === "create") return renderCreateResult(result as ResyncResult);
      if (open.kind === "connect") return renderConnectResult(result as ConnectResult, open);
      return renderUnlinkResult(result as UnlinkResult);
    }
    if (open.kind === "create") return renderCreateConfirmation(open);
    if (open.kind === "connect") return renderConnectConfirmation(open);
    return renderUnlinkConfirmation(open);
  };

  return (
    <Modal id="subitem-destructive" show={isOpen} onClose={handleClose} size="medium">
      <ModalBasicLayout>
        <ModalHeader title={request ? TITLES[request.kind] : "Create missing subitem"} className="px-8" />
        <ModalContent>{request ? renderBody(request) : null}</ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={
          result
            ? { onClick: handleClose, text: "Done" }
            : {
                onClick: handleConfirm,
                text: request ? CONFIRM_LABELS[request.kind] : "Create",
                loading,
                disabled: confirmDisabled,
                // Connect is additive — it links and creates, overwrites and deletes nothing —
                // so it does not wear the destructive colour the other two earn.
                color: request?.kind === "connect" ? "primary" : "negative",
              }
        }
        secondaryButton={result ? undefined : { onClick: handleClose, text: "Cancel", disabled: loading }}
      />
    </Modal>
  );
};

export default SubitemDestructiveModal;
